import { useState, useEffect } from "react"
import { auth } from "../../firebase/config"
import Location from "../../hooks/Location"
import { useHistory } from "react-router-dom"
import Hostname from '../../hooks/Hostname'
import { client } from '../../hooks/Client'
import firebase from '../../firebase/compat'
import spinner from '../../images/spinner-ripple.svg'
import verifyAccountBanner from '../../images/verify-account-banner.jpg'

// De verificatielink draagt een eenmalige token (`?token=...`); die bewijst dat
// iemand de mailbox heeft. Zonder token (een mail van vóór 2026-10-30, of na
// inloggen met een niet-geverifieerd account) kan een ingelogde gebruiker een
// nieuwe mail aanvragen. Publieke pagina: alles via Cloud Functions (ADR-022).
// AI-CONTEXT: See docs/features/mfa-totp.md

/**
 * @typedef {'verified'|'used'|'expired'|'not-found'|'wrong-user'|'email-changed'} VerifyStatus
 * @typedef {'sent'|'too-soon'|'already-verified'} ResendStatus
 */

/** Wat de gebruiker leest bij elke uitkomst van confirmEmailVerification. */
const VERIFY_MESSAGES = {
    used: 'Deze link is al gebruikt. Je account is al geverifieerd; je kunt inloggen.',
    expired: 'Deze link is verlopen. Vraag een nieuwe verificatiemail aan.',
    'not-found': 'Deze link werkt niet (meer). Vraag een nieuwe verificatiemail aan.',
    'wrong-user': "Deze link hoort bij een ander account dan waarmee je nu bent ingelogd. Klik op 'Uitloggen' en open de link in de mail daarna opnieuw.",
    'email-changed': 'Je e-mailadres is gewijzigd sinds deze mail. Vraag een nieuwe verificatiemail aan.',
}

/** Wat de gebruiker leest bij elke uitkomst van resendVerificationMail. */
const RESEND_MESSAGES = {
    sent: 'We hebben een nieuwe mail gestuurd naar je inlogadres. Klik op de knop in die mail.',
    'too-soon': 'Je hebt net al een mail aangevraagd. Probeer het over een minuut opnieuw.',
    'already-verified': 'Je e-mailadres is al geverifieerd; je kunt inloggen.',
}

/** @param {string} name @returns {Function} callable in europe-west1 */
const callable = (name) => firebase.app().functions('europe-west1').httpsCallable(name)

const NotApproved = () => {
    const [loading, setLoading] = useState(false)
    const [verifyStatus, setVerifyStatus] = useState(/** @type {VerifyStatus | null} */ (null))
    const [resendStatus, setResendStatus] = useState(/** @type {ResendStatus | null} */ (null))
    const [error, setError] = useState('')
    // null = nog onbekend; dan beloven we niets (geen knop, geen 'log in').
    const [signedIn, setSignedIn] = useState(/** @type {boolean | null} */ (null))

    const userId = Location()[3]
    const token = new URLSearchParams(window.location.search).get('token')
    const host = Hostname()
    const history = useHistory()

    useEffect(() => {
        const unsub = auth.onAuthStateChanged((user) => setSignedIn(!!user))
        return () => unsub()
    }, [])

    const verifyAccount = async () => {
        setLoading(true)
        setError('')
        try {
            const result = await callable('confirmEmailVerification')({ token, userId })
            if (result.data.status === 'verified') {
                history.push('/login')
                return
            }
            setVerifyStatus(result.data.status)
        } catch (e) {
            setError(`Verifiëren is mislukt: ${e.message}`)
        } finally {
            setLoading(false)
        }
    }

    const resendMail = async () => {
        setLoading(true)
        setError('')
        try {
            const result = await callable('resendVerificationMail')({
                client,
                hostname: window.location.hostname,
            })
            setResendStatus(result.data.status)
        } catch (e) {
            setError(`De mail kon niet worden verstuurd: ${e.message}`)
        } finally {
            setLoading(false)
        }
    }

    // Ingelogd met een ander account: naar /login helpt niet, want Login stuurt
    // een bestaande sessie meteen door. Een volledige laadbeurt, zodat het
    // inlogscherm zonder de state van dat account begint (zoals Profile.js).
    const logOutAndLogIn = async () => {
        await auth.signOut()
        window.location.assign('/login')
    }

    const canLogIn = verifyStatus === 'used' || resendStatus === 'already-verified'
    const showVerify = !!token && verifyStatus === null
    const showResend = !showVerify && !canLogIn && resendStatus !== 'sent'

    const actions = () => {
        if (loading) return <img src={spinner} alt="" />
        if (showVerify) return <button onClick={verifyAccount}>Verifieer account</button>
        if (canLogIn) return <button onClick={() => history.push('/login')}>Naar inloggen</button>
        if (verifyStatus === 'wrong-user' && signedIn) return <button onClick={logOutAndLogIn}>Uitloggen</button>
        if (!showResend || signedIn === null) return null
        if (signedIn) return <button onClick={resendMail}>Stuur een nieuwe verificatiemail</button>
        return (
            <>
                <p>Log in om een nieuwe verificatiemail aan te vragen.</p>
                <button onClick={() => history.push('/login')}>Inloggen</button>
            </>
        )
    }

    return (
        <div id='not-approved-container'>
            <div className="approval-message-container">
                <img src={verifyAccountBanner} alt="" />
                <h1>Verifieer je {host.Name} account</h1>
                {verifyStatus && VERIFY_MESSAGES[verifyStatus] && <p>{VERIFY_MESSAGES[verifyStatus]}</p>}
                {resendStatus && <p>{RESEND_MESSAGES[resendStatus]}</p>}
                {error && <p role="alert" className="not-approved-error">{error}</p>}
                {actions()}
            </div>
        </div>
    )
}

export default NotApproved
